import { useEffect, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { STANDARD_UNITS, pricePerReferenceUnit } from "@/lib/supplierHelpers";

export const ProductPricesDialog = ({ product, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ prices: [], reference_unit: product?.reference_unit });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    amount: "", quantity: "1", unit: product?.reference_unit || "kg",
    effective_date: new Date().toISOString().slice(0, 10), note: "",
  });

  const fetchPrices = useCallback(async () => {
    if (!product) return;
    try {
      setLoading(true);
      const { data } = await api.getProductPrices(product.id);
      setData(data);
    } catch (_e) {
      toast.error("Erreur chargement historique");
    } finally {
      setLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (open) fetchPrices();
  }, [open, fetchPrices]);

  const handleAdd = async () => {
    if (!form.amount || !form.unit || !form.effective_date) {
      toast.error("Montant, unité et date requis");
      return;
    }
    try {
      await api.addProductPrice(product.id, {
        amount: parseFloat(form.amount),
        quantity: parseFloat(form.quantity) || 1,
        unit: form.unit,
        effective_date: form.effective_date,
        note: form.note || null,
      });
      toast.success("Prix ajouté");
      setAddOpen(false);
      setForm({
        amount: "", quantity: "1", unit: product.reference_unit || "kg",
        effective_date: new Date().toISOString().slice(0, 10), note: "",
      });
      fetchPrices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    }
  };

  const handleDelete = async (priceId) => {
    if (!window.confirm("Supprimer ce prix ?")) return;
    try {
      await api.deleteProductPrice(priceId);
      toast.success("Prix supprimé");
      fetchPrices();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  const chartData = (data.prices || [])
    .filter((p) => p.price_per_reference_unit != null)
    .map((p) => ({
      date: p.effective_date,
      price: Number(p.price_per_reference_unit),
    }));

  // Compute variation vs previous
  const prices = data.prices || [];
  const prevEntry = prices.length >= 2 ? prices[prices.length - 2] : null;
  const lastEntry = prices.length ? prices[prices.length - 1] : null;
  const variation = (lastEntry?.price_per_reference_unit != null && prevEntry?.price_per_reference_unit != null)
    ? ((lastEntry.price_per_reference_unit - prevEntry.price_per_reference_unit) / prevEntry.price_per_reference_unit) * 100
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Historique de prix — {product?.name}
            {product?.reference && <span className="ml-2 text-sm text-muted-foreground font-mono">({product.reference})</span>}
          </DialogTitle>
          <DialogDescription>
            Suivi de l'évolution des prix (ramenés à l'unité de référence <strong>{data.reference_unit}</strong>).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Card className="flex-1 min-w-[220px]">
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">Prix courant / {data.reference_unit}</div>
                  <div className="text-2xl font-bold">
                    {lastEntry?.price_per_reference_unit != null
                      ? `${formatCurrency(lastEntry.price_per_reference_unit)}/${data.reference_unit}`
                      : "—"}
                  </div>
                  {variation != null && (
                    <div className={`text-xs flex items-center gap-1 mt-1 ${variation > 0 ? "text-red-600" : variation < 0 ? "text-green-600" : "text-muted-foreground"}`}>
                      {variation > 0 ? <TrendingUp className="h-3 w-3" /> : variation < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                      {variation > 0 ? "+" : ""}{variation.toFixed(1)}% vs précédent
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="flex-1 min-w-[220px]">
                <CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">Points de référence</div>
                  <div className="text-2xl font-bold">{prices.length}</div>
                  <div className="text-xs text-muted-foreground">Unité de référence : {data.reference_unit}</div>
                </CardContent>
              </Card>
            </div>

            {chartData.length >= 2 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Évolution du prix</CardTitle></CardHeader>
                <CardContent className="pl-0">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`${formatCurrency(v)}/${data.reference_unit}`, "Prix"]} />
                      <Line type="monotone" dataKey="price" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between">
              <h3 className="font-medium">Historique</h3>
              <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-product-price-btn">
                <Plus className="h-4 w-4 mr-1" /> Nouveau prix
              </Button>
            </div>

            {prices.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded">
                Aucun prix enregistré.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Quantité × Unité</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Prix / {data.reference_unit}</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prices.slice().reverse().map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono">{new Date(p.effective_date).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell>{p.quantity} × {p.unit}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(p.amount)}</TableCell>
                      <TableCell>
                        {p.price_per_reference_unit != null
                          ? <span className="font-mono">{formatCurrency(p.price_per_reference_unit)}/{data.reference_unit}</span>
                          : <Badge variant="outline" className="text-xs">non convertible</Badge>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.note || "-"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-price-${p.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau prix</DialogTitle>
            <DialogDescription>Enregistrer un nouveau tarif daté pour ce produit.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantité</Label>
                  <Input
                    type="number" step="0.01" value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    data-testid="price-quantity"
                  />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="price-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STANDARD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Montant (€) *</Label>
                  <Input
                    type="number" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="price-amount"
                  />
                </div>
                <div>
                  <Label>Date d'effet *</Label>
                  <Input
                    type="date" value={form.effective_date}
                    onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                    data-testid="price-date"
                  />
                </div>
              </div>
              <div>
                <Label>Note</Label>
                <Input
                  value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Ex: hausse fournisseur, offre promo..."
                />
              </div>
              {form.amount && form.quantity && form.unit && product && (() => {
                const ppr = pricePerReferenceUnit(product, parseFloat(form.amount), parseFloat(form.quantity), form.unit);
                return (
                  <div className="p-3 rounded border bg-sky-50 text-sm">
                    {ppr != null
                      ? <>Ramené en prix par <strong>{product.reference_unit}</strong> : <strong>{formatCurrency(ppr)}/{product.reference_unit}</strong></>
                      : <span className="text-amber-700">Conversion impossible avec les équivalences actuelles. Ajoutez une équivalence pour « {form.unit} » dans la fiche produit.</span>}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
              <Button onClick={handleAdd} data-testid="save-price-btn">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default ProductPricesDialog;
